// Inline rename control — a label with a hover-revealed pencil. Click the
// pencil (or double-click the text) to edit; Enter/blur saves, Escape cancels.
// Mirrors the Riffloop extension's stem-rename affordance.
import { useEffect, useRef, useState } from 'react';
import { Icons } from './studioIcons';

interface Props {
  value: string;
  onSave: (next: string) => void;
  /** Accent for the edit field's focus ring (e.g. the stem's color). */
  color?: string;
  title?: string;
}

export function EditableName({ value, onSave, color, title }: Props) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function commit() {
    const next = inputRef.current?.value.trim() ?? '';
    setEditing(false);
    if (next && next !== value) onSave(next);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="st-name-edit st-mono"
        defaultValue={value}
        maxLength={48}
        style={color ? ({ ['--st-name-accent']: color } as React.CSSProperties) : undefined}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <span className="st-name-wrap" title={title ?? 'Rename'}>
      <span className="st-name-txt" onDoubleClick={() => setEditing(true)}>
        {value}
      </span>
      <button
        type="button"
        className="st-name-pencil"
        aria-label="Rename"
        title="Rename"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
      >
        {Icons.pencil({ size: 11 })}
      </button>
    </span>
  );
}
